import { useMemo, useState, useEffect } from 'react';
import { Box, Menu, MenuItem, IconButton, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const QuickFilters = ({
  onApply,
  visibleCount,
  tzOffsetMinutes,
  presets,
  activeKey,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  const upMd = useMediaQuery(theme.breakpoints.up('md'));
  const upLg = useMediaQuery(theme.breakpoints.up('lg'));
  const upXl = useMediaQuery(theme.breakpoints.up('xl'));

  const autoVisibleCount = upXl ? 10 : upLg ? 7 : upMd ? 5 : upSm ? 4 : 3;
  const effectiveVisibleCount = Number.isFinite(visibleCount)
    ? visibleCount
    : autoVisibleCount;

  const timeZone = 'America/Sao_Paulo';

  const toYMD = d => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };
  const addMonths = (d, n) => {
    const x = new Date(d);
    x.setMonth(x.getMonth() + n);
    return x;
  };
  const addYears = (d, n) => {
    const x = new Date(d);
    x.setFullYear(x.getFullYear() + n);
    return x;
  };

  const monthStart = d => new Date(d.getFullYear(), d.getMonth(), 1);

  const defaultPresets = useMemo(() => {
    const today = new Date();

    const lastNDays = n => ({
      publishedAtFrom: toYMD(addDays(today, -(n - 1))),
      publishedAtTo: toYMD(today),
    });

    const lastNMonths = n => {
      const start = monthStart(addMonths(today, -(n - 1)));
      return { publishedAtFrom: toYMD(start), publishedAtTo: toYMD(today) };
    };

    const lastNYears = n => ({
      publishedAtFrom: toYMD(addYears(today, -n)),
      publishedAtTo: toYMD(today),
    });

    return [
      { key: 'last7', label: 'Últimos 7 dias', compute: () => lastNDays(7) },
      { key: 'last30', label: 'Últimos 30 dias', compute: () => lastNDays(30) },
      {
        key: 'thisMonth',
        label: 'Este mês',
        compute: () => ({
          publishedAtYear: String(
            new Intl.DateTimeFormat('en-CA', {
              timeZone,
              year: 'numeric',
            }).format(today)
          ),
          publishedAtMonth: new Intl.DateTimeFormat('en-CA', {
            timeZone,
            month: '2-digit',
          }).format(today),
        }),
      },
      {
        key: 'prevMonth',
        label: 'Mês anterior',
        compute: () => {
          const prev = addMonths(today, -1);
          return {
            publishedAtYear: new Intl.DateTimeFormat('en-CA', {
              timeZone,
              year: 'numeric',
            }).format(prev),
            publishedAtMonth: new Intl.DateTimeFormat('en-CA', {
              timeZone,
              month: '2-digit',
            }).format(prev),
          };
        },
      },
      {
        key: 'currentYear',
        label: 'Ano atual',
        compute: () => {
          const y = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
          }).format(today);
          return {
            publishedAtFrom: `${y}-01-01`,
            publishedAtTo: toYMD(today),
          };
        },
      },
      {
        key: 'lastYear',
        label: 'Ano anterior',
        compute: () => {
          const yNum =
            Number(
              new Intl.DateTimeFormat('en-CA', {
                timeZone,
                year: 'numeric',
              }).format(today)
            ) - 1;
          const from = `${yNum}-01-01`;
          const to = toYMD(new Date(yNum, 11, 31));
          return { publishedAtFrom: from, publishedAtTo: to };
        },
      },
      { key: 'last60', label: 'Últimos 60 dias', compute: () => lastNDays(60) },
      { key: 'last90', label: 'Últimos 90 dias', compute: () => lastNDays(90) },
      {
        key: 'last180',
        label: 'Últimos 180 dias',
        compute: () => lastNDays(180),
      },

      {
        key: 'last3months',
        label: 'Últimos três meses',
        compute: () => lastNMonths(3),
      },
      {
        key: 'last6months',
        label: 'Últimos seis meses',
        compute: () => lastNMonths(6),
      },
      {
        key: 'last12months',
        label: 'Últimos doze meses',
        compute: () => lastNMonths(12),
      },

      { key: 'last1year', label: '1 ano', compute: () => lastNYears(1) },
      { key: 'last2years', label: '2 anos', compute: () => lastNYears(2) },
      { key: 'last3years', label: '3 anos', compute: () => lastNYears(3) },
      { key: 'last4years', label: '4 anos', compute: () => lastNYears(4) },
      { key: 'last5years', label: '5 anos', compute: () => lastNYears(5) },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = presets?.length ? presets : defaultPresets;
  const [visibleKeys, setVisibleKeys] = useState(() =>
    items.slice(0, effectiveVisibleCount).map(it => it.key)
  );

  useEffect(() => {
    setVisibleKeys(prev => {
      const base = (prev || []).filter(k => items.some(it => it.key === k));
      const filled = [...base];
      if (
        activeKey &&
        !filled.includes(activeKey) &&
        items.some(it => it.key === activeKey)
      ) {
        filled.unshift(activeKey);
      }
      for (const it of items) {
        if (filled.length >= effectiveVisibleCount) break;
        if (!filled.includes(it.key)) filled.push(it.key);
      }
      return filled.slice(0, effectiveVisibleCount);
    });
  }, [items, effectiveVisibleCount, activeKey]);

  const promoteToVisible = key => {
    setVisibleKeys(prev => {
      const base = (prev || []).filter(k => k !== key);
      const next = [key, ...base].slice(0, effectiveVisibleCount);
      return next;
    });
  };

  const visibleItems = items
    .filter(it => visibleKeys.includes(it.key))
    .sort((a, b) => visibleKeys.indexOf(a.key) - visibleKeys.indexOf(b.key));

  const overflowItems = items.filter(it => !visibleKeys.includes(it.key));

  const applyPreset = preset => {
    promoteToVisible(preset.key);
    const payload = preset.compute();
    onApply({ preset, ...payload });
    setAnchorEl(null);
  };

  const Pill = ({ item }) => {
    const isActive = activeKey === item.key;
    return (
      <Box
        onClick={() => applyPreset(item)}
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          border: isActive ? '2px solid #1361A4' : '1px solid #D7E8F7',
          background: isActive ? '#E8F2FF' : '#FFF',
          color: '#1361A4',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          '&:hover': { background: '#F1F7FF', borderColor: '#1462A5' },
        }}
        title={item.label}
        role="button">
        {item.label}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        p: 1.5,
        border: '1px solid #D7E8F7',
        borderRadius: 1.5,
        background: '#FAFCFF',
      }}>
      {visibleItems.map(it => (
        <Pill key={it.key} item={it} />
      ))}

      {!!overflowItems.length && (
        <>
          <Tooltip title="Mais filtros">
            <IconButton
              size="small"
              onClick={e => setAnchorEl(e.currentTarget)}
              sx={{
                border: '1px solid #D7E8F7',
                background: '#FFF',
                '&:hover': { background: '#F1F7FF', borderColor: '#1462A5' },
              }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}>
            {overflowItems.map(it => (
              <MenuItem key={it.key} onClick={() => applyPreset(it)}>
                {it.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default QuickFilters;
